<template>
  <table-page
    v-model:search-term="searchTerm"
    v-model:selected-filters="selectedAnalysisStatusFilter"
    title="Contestações"
    :loading="isInfractionsLoading"
    :search-placeholder="'Documento ou E2E Pix'"
    @clean-filter="onCleanFilter"
    @submit-filters="submitFilter"
  >
    <template #bg-date-filter>
      <bg-date-filter
        v-model:start-date="period.startDate"
        v-model:end-date="period.endDate"
        full-width
        @update:start-date="submitFilter"
        @update:end-date="submitFilter"
      />
    </template>
    <template #toolbar-buttons>
      <bg-columns>
        <bg-column width="content">
          <bg-button
            v-if="infractionsList.length > 0"
            size="sm"
            :disabled="!infractionsList.some(infraction => infraction.selected)"
            @click="openInfractionsBatchApproveDialog"
          >
            Aprovar selecionados ({{ selectedInfractions.length }})
          </bg-button>
        </bg-column>
        <bg-column>
          <bg-menu
            v-if="infractionsList.length > 0"
          >
            <template #button="args">
              <bg-button
                v-bind="args"
                :disabled="disabled"
                size="sm"
                right-icon="caret-down"
              >
                Exportar
              </bg-button>
            </template>
            <bg-menu-item @click="openInfractionsExportDialog">
              Relatório CSV
            </bg-menu-item>
          </bg-menu>
        </bg-column>
      </bg-columns>
    </template>
    <template #bg-filters>
      <bg-filter
        key="status"
        label="Status"
      >
        <bg-filter-option
          v-for="(statusName, statusKey) in analysisStatus"
          :key="statusKey"
          :label="statusName"
        />
      </bg-filter>
    </template>
    <template #content>
      <infractions-list
        v-if="infractionsList.length > 0"
        :infractions-list="infractionsList"
      />
      <tr-empty-search-result
        v-if="!isInfractionsLoading && infractionsList.length === 0"
        text="Não há infrações a serem listadas"
      />
    </template>
    <template #pagination>
      <bg-pagination
        v-if="cursorPagination.pagination.next || cursorPagination.pagination.previous"
        v-model:page-cursor="cursorPagination.pagination.pageCursor"
        :next="cursorPagination.pagination.next"
        :previous="cursorPagination.pagination.previous"
      />
    </template>
    <template #items-per-page>
      <tr-button
        v-if="infractionsList.length > 0"
        dropdown="true"
        :label="selectedItemsPerPage.name"
        text-variant="body"
        variant="text"
      >
        <tr-dropdown-item
          v-for="option in itemsPerPageOptions"
          :key="option.key"
          @click="onSelectedItemsPerPage(option)"
        >
          {{ option.name }}
        </tr-dropdown-item>
      </tr-button>
    </template>
  </table-page>
  <infractions-batch-approve-dialog
    ref="infractionsBatchApproveDialog"
    :selected-infractions="selectedInfractions"
    @approved="fetchInfractions"
  />
  <infractions-export-dialog
    ref="infractionsExportDialog"
    :export-filters="exportFilters"
  />
</template>

<script setup>
import TablePage from '@/commons/components/presentational/TablePage.vue';
// eslint-disable-next-line no-unused-vars
import InfractionsList from '@/apps/contestations/infractions/presentational/InfractionsList.vue';
// eslint-disable-next-line no-unused-vars
import InfractionsBatchApproveDialog from '@/apps/contestations/infractions/presentational/InfractionsBatchApproveDialog.vue';
// eslint-disable-next-line no-unused-vars
import InfractionsExportDialog from '@/apps/contestations/infractions/presentational/InfractionsExportDialog.vue';
import {
  BgPagination,
  BgDateFilter,
  BgFilter,
  BgFilterOption,
  BgButton,
  BgMenu,
  BgMenuItem,
  BgColumns,
  BgColumn
} from '@transfeeradev/bridge';
import { usePagination } from '@/apps/payin/payins/pages/usePagination';
import { listInfractions } from '@/apps/contestations/infractions/services/infractions';
import { useErrorHandler } from '@/commons/composables/useErrorHandler';
import formatDate from '@/commons/formatters/formatDate';
import analysisStatus from '@/apps/contestations/infractions/constants/analysisStatus';
import { ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import moment from '@transfeeradev/moment/src/plugins/moment-business-day';
import { computed } from 'vue';

const { handleError } = useErrorHandler();
const infractionsList = ref([]);

const searchTerm = ref('');
const period = ref({
  startDate: moment().startOf('month').toDate(),
  endDate: moment().endOf('month').toDate()
});

const infractionsBatchApproveDialog = ref();
const infractionsExportDialog = ref();
const exportFilters = ref({});

const openInfractionsBatchApproveDialog = () => {
  infractionsBatchApproveDialog.value.open();
};

const openInfractionsExportDialog = () => {
  infractionsExportDialog.value.open();
  exportFilters.value = {
    infractionStartDate: {
      label: 'Data de início',
      requestValue: period.value.startDate,
      displayValue: formatDate(period.value.startDate)
    },
    infractionEndDate: {
      label: 'Data de término',
      requestValue: period.value.endDate,
      displayValue: formatDate(period.value.endDate)
    },
    searchTerm: {
      label: 'Termo de busca',
      requestValue: searchTerm.value,
      displayValue: searchTerm.value
    },
    infractionAnalysisStatus: {
      label: 'Status da contestação',
      displayValue: Object.keys(selectedAnalysisStatusFilter.value.status)
        .filter(key => selectedAnalysisStatusFilter.value.status[key])
        .map(key => analysisStatus[key])
        .join(', '),
      requestValue: selectedAnalysisStatusFilter.value.status
    }
  };
};

const selectedInfractions = computed(() => {
  return infractionsList.value.filter(infraction => infraction.selected);
});

const itemsPerPageOptions = [
  {
    key: '10',
    name: 'Mostrar 10 items'
  },
  {
    key: '20',
    name: 'Mostrar 20 items'
  },
  {
    key: '30',
    name: 'Mostrar 30 items'
  },
  {
    key: '50',
    name: 'Mostrar 50 items'
  },
  {
    key: '100',
    name: 'Mostrar 100 items'
  }
];

const selectedItemsPerPage = ref(itemsPerPageOptions.find(option => option.key === '10'));
const selectedAnalysisStatusFilter = ref({
  status: {}
});

const isInfractionsLoading = ref(false);

const onSelectedItemsPerPage = option => {
  selectedItemsPerPage.value = option;
  resetPagination();
  fetchInfractions();
};

const resetPagination = () => {
  cursorPagination.pagination.pageCursor = 'enabled';
};

const submitFilter = filters => {
  if (filters?.status) {
    selectedAnalysisStatusFilter.value.status = filters.status;
  }
  resetPagination();
  fetchInfractionsDebounced();
};

const onCleanFilter = () => {
  selectedAnalysisStatusFilter.value = { status: {} };
  resetPagination();
  fetchInfractions();
};

const { cursorPagination } = usePagination(() => {
  fetchInfractions();
});

const getPaginationData = () => {
  return {
    pageCursor: cursorPagination.pagination.pageCursor === 'enabled' ? null : cursorPagination.pagination.pageCursor,
    pageSize: selectedItemsPerPage.value.key
  };
};

const fetchInfractionsDebounced = useDebounceFn(() => {
  isInfractionsLoading.value = true;
  fetchInfractions();
}, 300);

const fetchInfractions = async () => {
  try {
    isInfractionsLoading.value = true;
    const data = await listInfractions({
      ...getPaginationData(),
      infractionStartDate: period.value.startDate,
      infractionEndDate: period.value.endDate,
      searchTerm: searchTerm.value,
      infractionAnalysisStatus: selectedAnalysisStatusFilter.value.status
    });
    infractionsList.value = data.items;
    cursorPagination.pagination.next = data.meta.next;
    cursorPagination.pagination.previous = data.meta.previous;
  } catch (error) {
    handleError(error, 'Ocorreu um erro ao buscar infrações Pix');
  } finally {
    isInfractionsLoading.value = false;
  }
};
fetchInfractions();
</script>
